import type { AccountSummary } from '@personal-os/shared';
import QRCode from 'qrcode';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { totpConfirm, totpDisable, totpStart } from './api';
import { describeAuthError } from './passkeys';
import type { RunSensitive } from './PasskeysCard';

interface Setup {
  secret: string;
  uri: string;
}

export function AuthenticatorCard({
  account,
  run,
  refresh,
  onCodes,
}: {
  account: AccountSummary;
  run: RunSensitive;
  refresh: () => Promise<unknown>;
  onCodes: (codes: string[]) => void;
}) {
  const id = useId();
  const [setup, setSetup] = useState<Setup | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!setup) return;
    let cancelled = false;
    void QRCode.toDataURL(setup.uri, { margin: 1, width: 224, errorCorrectionLevel: 'M' }).then(
      (url) => {
        if (!cancelled) setQr(url);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [setup]);

  async function guarded(action: () => Promise<unknown>) {
    setBusy(true);
    setMessage(null);
    try {
      await run(action);
    } catch (err) {
      setMessage(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  const start = () =>
    guarded(async () => {
      const next = await totpStart();
      setQr(null);
      setSetup(next);
    });

  const turnOff = () =>
    guarded(async () => {
      await totpDisable();
      setSetup(null);
      await refresh();
    });

  async function confirm(e: FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await totpConfirm(code.trim());
      setSetup(null);
      setCode('');
      await refresh();
      if (result.recoveryCodes) onCodes(result.recoveryCodes);
    } catch (err) {
      setMessage(describeAuthError(err));
    } finally {
      setBusy(false);
    }
  }

  async function copyKey() {
    if (!setup) return;
    try {
      await navigator.clipboard.writeText(setup.secret.replace(/\s/g, ''));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <section className="card" aria-labelledby="totp-title" data-testid="authenticator">
      <h2 id="totp-title" className="card-heading">
        Authenticator app
      </h2>
      <p className="hint">
        A backup way to sign in if you lose your phone, using an app like Google Authenticator.
      </p>

      {account.authenticator.enabled && !setup && (
        <>
          <p className="status-text" data-state="on">
            <span className="dot dot-good" aria-hidden="true" /> On
          </p>
          <button type="button" disabled={busy} onClick={() => void turnOff()}>
            Turn off
          </button>
        </>
      )}

      {!account.authenticator.enabled && !setup && (
        <button type="button" className="primary" disabled={busy} onClick={() => void start()}>
          Set up authenticator app
        </button>
      )}

      {setup && (
        <div className="stack" data-testid="totp-setup">
          <ol className="steps">
            <li>
              Open your authenticator app and add an account. Scan this code, <strong>or</strong>{' '}
              choose "enter a setup key" and type the key below.
            </li>
            <li>Enter the 6-digit code the app shows.</li>
          </ol>
          {qr && (
            <img
              className="qr"
              src={qr}
              alt="QR code for your authenticator app"
              width={224}
              height={224}
            />
          )}
          <div className="copy-field">
            <label htmlFor={`${id}-key`}>Setup key</label>
            <div className="copy-row">
              <input
                id={`${id}-key`}
                type="text"
                readOnly
                value={setup.secret}
                spellCheck={false}
              />
              <button type="button" onClick={() => void copyKey()}>
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>
          <form onSubmit={(e) => void confirm(e)} className="stack">
            <label htmlFor={`${id}-code`}>6-digit code</label>
            <input
              id={`${id}-code`}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <div className="button-row">
              <button type="submit" className="primary" disabled={busy || code.trim() === ''}>
                Turn on
              </button>
              <button type="button" onClick={() => setSetup(null)}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {message && (
        <p role="alert" className="error-text">
          {message}
        </p>
      )}
    </section>
  );
}
